import React, { useEffect, useRef, useState } from "react";
// @ts-ignore
import Select from "react-select";
import withContext from "../../../tools/withContext";
import constants from "../../../tools/constants";

const { USER_PROJECTS_PATH } = constants.paths;

const MENU_PORTAL_Z_INDEX = 9999;

// ─── Styles ───────────────────────────────────────────────────────────────────

const selectStyles: any = {
  control: (base: any, state: any) => ({
    ...base,
    borderRadius: 4,
    backgroundColor: "#fff",
    border: state.isFocused
      ? "1px solid #80bdff"
      : "1px solid rgba(72,72,72,.2)",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(0,123,255,.25)" : "none",
    fontSize: 16,
    minHeight: 42,
    cursor: "text",
    "&:hover": {
      border: state.isFocused
        ? "1px solid #80bdff"
        : "1px solid rgba(72,72,72,.4)",
    },
  }),
  valueContainer: (base: any) => ({
    ...base,
    padding: "2px 12px",
    flexWrap: "wrap",
  }),
  input: (base: any) => ({
    ...base,
    fontSize: 16,
    margin: 0,
    padding: "2px 0",
  }),
  placeholder: (base: any) => ({
    ...base,
    color: "#aaa",
    fontSize: 16,
  }),
  option: (base: any, state: any) => ({
    ...base,
    fontSize: 16,
    backgroundColor: state.isFocused ? "rgba(0,123,255,.05)" : "white",
    color: "#333",
    cursor: "pointer",
    padding: "8px 12px",
  }),
  menu: (base: any) => ({
    ...base,
    zIndex: MENU_PORTAL_Z_INDEX,
    boxShadow: "0 2px 8px rgba(0,0,0,.15)",
    borderRadius: 4,
  }),
  menuPortal: (base: any) => ({
    ...base,
    zIndex: MENU_PORTAL_Z_INDEX,
  }),
  multiValue: (base: any) => ({
    ...base,
    backgroundColor: "rgba(0,123,255,.12)",
    borderRadius: 3,
    margin: "2px 4px 2px 0",
  }),
  multiValueLabel: (base: any) => ({
    ...base,
    color: "#0056b3",
    fontSize: 13,
    padding: "2px 4px 2px 6px",
  }),
  multiValueRemove: (base: any) => ({
    ...base,
    color: "#0056b3",
    paddingRight: 4,
    "&:hover": {
      backgroundColor: "rgba(0,123,255,.25)",
      color: "#003d80",
    },
  }),
  dropdownIndicator: () => ({ display: "none" }),
  indicatorSeparator: () => ({ display: "none" }),
  clearIndicator: (base: any) => ({
    ...base,
    padding: "0 8px",
    color: "#aaa",
    "&:hover": { color: "#666" },
  }),
};

// ─── Component ────────────────────────────────────────────────────────────────

type ProjectOption = { value: string; label: string; client?: string };

interface Props {
  value: ProjectOption[];
  onChange: (values: ProjectOption[]) => void;
  ctx: any;
  clientId?: string;
  labelingType?: string;
  required?: boolean;
}

const DEBOUNCE_MS = 400;
const PAGE_SIZE = 20;

const ProjectFilter = ({
  value,
  onChange,
  ctx,
  clientId,
  labelingType,
  required = false,
}: Props) => {
  const { appFetch } = ctx;
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState<ProjectOption[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [searchedQuery, setSearchedQuery] = useState("");
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Conserve les options sélectionnées même si elles ne sont plus dans la liste visible
  const selectedCacheRef = useRef<Map<string, ProjectOption>>(new Map());

  const toOptions = (projects: any[]): ProjectOption[] =>
    projects.map((p: any) => ({
      value: p.id,
      label: p.name,
      client: p.client?.name,
    }));

  const fetchPage = async (query: string, pageIndex: number) => {
    const params = new URLSearchParams({
      page: String(pageIndex),
      size: String(PAGE_SIZE),
    });
    if (query.trim()) params.set("searchTerm", query.trim());
    if (clientId) params.set("clientId", clientId);
    if (labelingType) params.set("labelingType", labelingType);
    const results = await appFetch(
      `${USER_PROJECTS_PATH}?${params.toString()}`,
    );
    const projects = Array.isArray(results) ? results : results?.content ?? [];
    const last: boolean = Array.isArray(results)
      ? projects.length < PAGE_SIZE
      : results?.last ?? projects.length < PAGE_SIZE;
    return { projects: toOptions(projects), hasMore: !last };
  };

  // Recherche avec debounce — réinitialise la pagination
  useEffect(() => {
    if (timerRef.current) clearTimeout(timerRef.current);

    if (!inputValue.trim()) {
      setOptions([]);
      setIsLoading(false);
      setHasMore(false);
      setSearchedQuery("");
      return;
    }

    timerRef.current = setTimeout(async () => {
      if (inputValue.length < 2) return;
      setIsLoading(true);
      try {
        const { projects, hasMore: more } = await fetchPage(inputValue, 0);
        projects.forEach((o) => selectedCacheRef.current.set(o.value, o));
        setOptions(projects);
        setPage(0);
        setHasMore(more);
        setSearchedQuery(inputValue);
      } catch {
        setOptions([]);
        setHasMore(false);
      }
      setIsLoading(false);
    }, DEBOUNCE_MS);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [inputValue]);

  // Charge la page suivante quand on arrive en bas de la liste
  const handleScrollToBottom = async () => {
    if (!hasMore || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const nextPage = page + 1;
      const { projects, hasMore: more } = await fetchPage(inputValue, nextPage);
      projects.forEach((o) => selectedCacheRef.current.set(o.value, o));
      setOptions((prev) => [...prev, ...projects]);
      setPage(nextPage);
      setHasMore(more);
    } catch {
      // silently fail
    }
    setIsLoadingMore(false);
  };

  const selectedOptions = value.map((v) => ({ value: v.value, label: v.label, client: v.client }));

  return (
    <div>
      <label style={{ fontSize: 16, display: "block", fontWeight: 200 }}>
        Projets
        {required && (
          <>
            {" "}
            <sup>*</sup>
          </>
        )}
      </label>
      <div style={{ marginTop: 8 }}>
        <Select
          isMulti
          options={options}
          isLoading={isLoading || isLoadingMore}
          inputValue={inputValue}
          onInputChange={(val: string, { action }: any) => {
            // Ignore les resets automatiques (sélection d'option, fermeture du menu)
            if (action === "input-change") setInputValue(val);
          }}
          onMenuScrollToBottom={handleScrollToBottom}
          styles={selectStyles}
          menuPortalTarget={
            typeof document !== "undefined" ? document.body : undefined
          }
          menuPosition="fixed"
          placeholder="Rechercher un projet..."
          noOptionsMessage={() =>
            searchedQuery && !isLoading ? "Aucun résultat" : null
          }
          loadingMessage={() =>
            isLoadingMore
              ? "Chargement de la page suivante..."
              : "Chargement..."
          }
          value={selectedOptions}
          onChange={(opts: any) =>
            onChange(opts ? opts.map((o: any) => ({ value: o.value, label: o.label, client: o.client })) : [])
          }
          isClearable
          closeMenuOnSelect={false}
          hideSelectedOptions={false}
          getOptionValue={(o: any) => o.value}
          getOptionLabel={(o: any) => o.label}
          filterOption={() => true}
          formatOptionLabel={(opt: any, { context }: any) => {
            const isSelected = value.some((p) => p.value === opt.value);
            if (context === "value") {
              return <span>{opt.label}</span>;
            }
            return (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: isSelected
                    ? "rgba(0,123,255,.08)"
                    : "transparent",
                  margin: "-8px -12px",
                  padding: "8px 12px",
                }}
              >
                <div>
                  <span
                    style={{
                      color: isSelected ? "#0056b3" : "#333",
                      fontWeight: isSelected ? 500 : 400,
                    }}
                  >
                    {opt.label}
                  </span>
                  {opt.client && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: 12,
                        color: isSelected ? "#4d94ff" : "#888",
                      }}
                    >
                      {opt.client}
                    </span>
                  )}
                </div>
                {isSelected && (
                  <span
                    style={{
                      color: "#007bff",
                      fontWeight: "bold",
                      fontSize: 16,
                      marginLeft: 8,
                    }}
                  >
                    ✓
                  </span>
                )}
              </div>
            );
          }}
        />
      </div>
    </div>
  );
};

export default withContext(ProjectFilter);
